import { Label, Checkbox, RadioGroup, RadioGroupItem } from '@/components/ui'
import type { ComboGroup } from '@/types/menu'
import type { ComboGroupSelection } from '@/types/cart'
import { formatPrice } from '@/utils/formatting'

interface ComboGroupSelectorProps {
  group: ComboGroup
  selectedOptions: ComboGroupSelection[]
  onToggle: (group: ComboGroup, optionId: string) => void
}

export function ComboGroupSelector({
  group,
  selectedOptions,
  onToggle,
}: ComboGroupSelectorProps) {
  const selectedCount = selectedOptions.length
  const maxReached = selectedCount >= group.maxSelections
  const isSingleSelection = group.maxSelections === 1
  const selectedValue = selectedOptions[0]?.optionId || ''

  const handleRadioChange = (optionId: string) => {
    onToggle(group, optionId)
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Label className="text-base font-semibold">
          {group.name}
          {isSingleSelection && <span className="text-destructive ml-1">*</span>}
        </Label>
        {!isSingleSelection && (
          <span className="text-sm text-muted-foreground">
            ({selectedCount}/{group.maxSelections})
          </span>
        )}
      </div>

      {isSingleSelection ? (
        <RadioGroup value={selectedValue} onValueChange={handleRadioChange} className="space-y-2" required>
          {group.options.map((option) => {
            const isSelected = selectedValue === option.id

            return (
              <div
                key={option.id}
                className={`
                  flex items-center space-x-3 border rounded-lg p-3 transition-colors cursor-pointer
                  ${isSelected ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'}
                `}
              >
                <RadioGroupItem value={option.id} id={option.id} />
                <div className="flex-1 flex items-center gap-3">
                  {option.ingredient.imageUrl && (
                    <div className="h-10 w-10 rounded-md overflow-hidden bg-muted flex-shrink-0">
                      <img
                        src={option.ingredient.imageUrl}
                        alt={option.ingredient.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <Label
                    htmlFor={option.id}
                    className="flex-1 cursor-pointer flex items-center justify-between"
                  >
                    <span className="font-medium">{option.ingredient.name}</span>
                    {option.additionalPrice > 0 && (
                      <span className="text-sm font-semibold text-muted-foreground">
                        +{formatPrice(option.additionalPrice, undefined, false)}
                      </span>
                    )}
                  </Label>
                </div>
              </div>
            )
          })}
        </RadioGroup>
      ) : (
        <div className="space-y-2">
          {group.options.map((option) => {
            const isSelected = selectedOptions.some((s) => s.optionId === option.id)
            const isDisabled = !isSelected && maxReached

            return (
              <div
                key={option.id}
                onClick={() => !isDisabled && onToggle(group, option.id)}
                className={`
                  flex items-center space-x-3 border rounded-lg p-3 cursor-pointer transition-colors
                  ${isSelected ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'}
                  ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}
                `}
              >
                <Checkbox
                  checked={isSelected}
                  disabled={isDisabled}
                  onCheckedChange={() => !isDisabled && onToggle(group, option.id)}
                />
                <div className="flex-1 flex items-center gap-3">
                  {option.ingredient.imageUrl && (
                    <div className="h-10 w-10 rounded-md overflow-hidden bg-muted flex-shrink-0">
                      <img
                        src={option.ingredient.imageUrl}
                        alt={option.ingredient.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <Label className="flex-1 cursor-pointer flex items-center justify-between">
                    <span className="font-medium">{option.ingredient.name}</span>
                    {option.additionalPrice > 0 && (
                      <span className="text-sm font-semibold text-muted-foreground">
                        +{formatPrice(option.additionalPrice, undefined, false)}
                      </span>
                    )}
                  </Label>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
